import { z } from "zod";

export const personSchema = z.object({
  // ── Identificação ──────────────────────────────────────────
  person_type: z.enum(["fisica", "juridica"]).default("fisica"),
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres").max(200),
  nickname: z.string().max(100).optional().nullable(),
  is_active: z.boolean().optional().default(true),

  // ── Documentos ─────────────────────────────────────────────
  cpf_cnpj: z.string().max(20).optional().nullable(),
  ie: z.string().max(40).optional().nullable(),
  im: z.string().max(20).optional().nullable(),
  rg: z.string().max(20).optional().nullable(),
  rg_issuer: z.string().max(10).optional().nullable(),
  rg_issue_date: z.string().optional().nullable(),
  driver_license: z.string().max(50).optional().nullable(),
  work_card: z.string().max(80).optional().nullable(),

  // ── Contato ────────────────────────────────────────────────
  email: z.string().email("Email inválido").optional().nullable().or(z.literal("")),
  phone: z.string().max(20).optional().nullable(),
  mobile: z.string().max(20).optional().nullable(),
  whatsapp: z.string().max(20).optional().nullable(),
  notes: z.string().max(1000).optional().nullable(),
  opt_in_marketing: z.boolean().optional().default(true),

  // ── Dados Pessoais (PF) ────────────────────────────────────
  birth_date: z.string().optional().nullable(),
  gender: z.enum(["masculino", "feminino", "outro"]).optional().nullable(),
  civil_status: z.enum(["solteiro", "casado", "divorciado", "viuvo", "uniao_estavel"]).optional().nullable(),
  profession: z.string().max(100).optional().nullable(),
  birthplace: z.string().max(200).optional().nullable(),

  // ── Filiação (PF) ──────────────────────────────────────────
  father_name: z.string().max(200).optional().nullable(),
  mother_name: z.string().max(200).optional().nullable(),
  spouse_name: z.string().max(200).optional().nullable(),
  spouse_phone: z.string().max(20).optional().nullable(),
  spouse_mobile: z.string().max(20).optional().nullable(),
  spouse_landline: z.string().max(20).optional().nullable(),

  // ── Pessoa de Recado ───────────────────────────────────────
  contact_person_name: z.string().max(60).optional().nullable(),
  contact_person_phone: z.string().max(20).optional().nullable(),
  contact_person_mobile: z.string().max(20).optional().nullable(),
  contact_person_residential: z.string().max(20).optional().nullable(),
  referral_person: z.string().max(200).optional().nullable(),

  // ── Tipos / Papéis ─────────────────────────────────────────
  is_customer: z.boolean().optional().default(false),
  is_supplier: z.boolean().optional().default(false),
  is_employee: z.boolean().optional().default(false),
  is_driver: z.boolean().optional().default(false),
  is_fleet_owner: z.boolean().optional().default(false),
  is_sales_rep: z.boolean().optional().default(false),
  is_accountant: z.boolean().optional().default(false),
  is_guest: z.boolean().optional().default(false),
  is_operator: z.boolean().optional().default(false),
  is_final_consumer: z.boolean().optional().default(true),
  is_exportable: z.boolean().optional().default(true),

  // ── Comercial ──────────────────────────────────────────────
  customer_group_id: z.coerce.number().optional().nullable(),
  billing_group_id: z.coerce.number().optional().nullable(),
  salesperson_id: z.coerce.number().optional().nullable(),
  price_table_id: z.coerce.number().int().min(1, "Tabela de preço é obrigatória"),
  route_id: z.coerce.number().optional().nullable(),
  discount_percentage: z.coerce.number().optional().nullable(),
  commission_percentage: z.coerce.number().optional().nullable(),
  credit_limit: z.coerce.number().optional().nullable(),
  allows_credit_sales: z.boolean().optional().default(true),
  credit_blocked: z.boolean().optional().default(false),
  credit_block_reason: z.string().max(200).optional().nullable(),
  spc_situation: z.string().max(1).optional().nullable(),
  invoice_due_days: z.coerce.number().optional().default(0),
  invoice_closing_day: z.coerce.number().optional().default(0),
  invoice_on_last_day: z.boolean().optional().default(false),

  // ── Dados bancários do favorecido (remessa CNAB / pagamento a fornecedor) ──
  supplier_bank_code: z.string().max(10).optional().nullable(),
  supplier_agency: z.string().max(20).optional().nullable(),
  supplier_agency_dv: z.string().max(2).optional().nullable(),
  supplier_account: z.string().max(20).optional().nullable(),
  supplier_account_dv: z.string().max(2).optional().nullable(),

  // ── Funcionário ────────────────────────────────────────────
  admission_date: z.string().optional().nullable(),
  job_function: z.string().max(60).optional().nullable(),
  salary: z.coerce.number().optional().nullable(),

  // ── Relacionamento (CRM) ─────────────────────────────────────────────────
  product_classification: z.string().max(200).optional().nullable(),
  desired_product: z.string().max(200).optional().nullable(),
  last_purchase_date: z.string().optional().nullable(),
  preferred_product_id: z.coerce.number().optional().nullable(),
  preferred_group_id: z.coerce.number().optional().nullable(),
  how_met: z.string().max(200).optional().nullable(),
  complaint: z.string().max(200).optional().nullable(),
  preferred_size: z.string().max(60).optional().nullable(),
  preferred_color: z.string().max(60).optional().nullable(),
  preferred_flavor: z.string().max(60).optional().nullable(),
  customer_classification: z.string().max(200).optional().nullable(),
  gave_up_purchase: z.boolean().optional().default(false),
  placed_order: z.boolean().optional().default(false),
  already_served: z.boolean().optional().default(false),
  needs_contact: z.boolean().optional().default(false),

  // ── Fiscal (NF-e / NFS-e / retenções) ──────────────────────
  tax_regime: z.coerce.number().optional().nullable(),        // CRT: 1 Simples, 2 Simples excesso, 3 Normal
  ie_indicator: z.coerce.number().optional().nullable(),      // indIEDest: 1 Contribuinte, 2 Isento, 9 Não contribuinte
  withhold_ir: z.boolean().optional().default(false),
  ir_percentage: z.coerce.number().optional().default(0),
  withhold_csll: z.boolean().optional().default(false),
  csll_percentage: z.coerce.number().optional().default(0),
  withhold_inss: z.boolean().optional().default(false),
  inss_percentage: z.coerce.number().optional().default(0),
  withhold_pis: z.boolean().optional().default(false),
  pis_percentage: z.coerce.number().optional().default(0),
  withhold_cofins: z.boolean().optional().default(false),
  cofins_percentage: z.coerce.number().optional().default(0),
  withhold_issqn: z.boolean().optional().default(false),
  issqn_percentage: z.coerce.number().optional().default(0),
  gov_agency_type: z.coerce.number().optional().nullable(),
  construction_id: z.string().max(20).optional().nullable(),  // ID da obra (RPS/ART)
  art_id: z.string().max(20).optional().nullable(),
  cbs_reduction_gov: z.coerce.number().optional().nullable(),

  // ── Fidelidade ─────────────────────────────────────────────
  earns_loyalty_points: z.boolean().optional().default(false),
  loyalty_points: z.coerce.number().optional().nullable(),
  loyalty_points_value: z.coerce.number().optional().nullable(),

  // ── IDs de referência ──────────────────────────────────────
  country_id: z.coerce.number().optional().nullable(),
  taxpayer_type_id: z.coerce.number().optional().nullable(),
  status_id: z.coerce.number().optional().nullable(),
});

export type PersonFormData = z.infer<typeof personSchema>;

export const addressSchema = z.object({
  address_type: z.enum(["residencial", "comercial", "cobranca", "entrega", "outro"]).default("residencial"),
  zip_code: z.string().max(40).optional().nullable(),
  street: z.string().min(1, "Logradouro é obrigatório").max(200),
  number: z.string().max(10).optional().nullable(),
  complement: z.string().max(80).optional().nullable(),
  neighborhood: z.string().max(80).optional().nullable(),
  city: z.string().min(1, "Cidade é obrigatória").max(200),
  state: z.string().min(2, "Estado é obrigatório").max(2),
  country: z.string().max(100).optional().default("Brasil"),
  distance_km: z.coerce.number().optional().nullable(),
  delivery_time_minutes: z.coerce.number().optional().nullable(),
  route_notes: z.string().max(500).optional().nullable(),
  is_primary: z.boolean().optional().default(false),
  is_active: z.boolean().optional().default(true),
  ibge_city_id: z.coerce.number().optional().nullable(),
  country_id: z.coerce.number().optional().nullable(),
});

export type AddressFormData = z.infer<typeof addressSchema>;
